import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { IconButton } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { AppTextField } from '../../../../customComponents/AppTextField';
import { AppUrlTextField } from '../../../../customComponents/AppUrlTextField';
import { AppNumberTextField } from '../../../../customComponents/AppNumberTextField';
import { AppDateTimePicker } from '../../../../customComponents/AppDateTimePicker';
import AuctioneerSearchPopup from '../../../../customComponents/AuctioneerSearchPopup';
import CmsPopup from '../../../../components/CmsPopup';
import { CmsPopupBtnType } from '../../../../components/CmsPopupBtn';

const PopupContainer = styled.div`
  padding-top: 20px;
  height: calc(100% - 40px);
  overflow: hidden;
`;

const MainContainer = styled.div`
  display: flex;
  gap: 24px;
  height: calc(100% - 80px);
  overflow: hidden;
`;

const LeftPanel = styled.div`
  flex: 1;
  border-right: 1px solid #e9ecef;
  padding-right: 24px;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  
  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const RightPanel = styled.div`
  flex: 1;
  padding-left: 24px;
  overflow: hidden;
`;

const SectionTitleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid #e9ecef;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
  text-align: left;
`;

const RequiredIndicator = styled.span`
  font-size: 14px;
  color: #dc3545;
  font-weight: 500;
`;

const Section = styled.div`
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 24px;
  background: #fafafa;
  margin-bottom: 24px;
`;

const SubSectionTitle = styled.h4`
  margin: 0 0 20px 0;
  font-size: 16px;
  font-weight: 600;
  color: #495057;
  text-align: left;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const DateRangeRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 20px;
  width: 100%;
`;

const DateFieldWrapper = styled.div`
  flex: 1;
`;

const DateSeparator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 16px;
  flex-shrink: 0;
  width: 40px;
  height: 48px;
  
  span {
    font-size: 18px;
    color: #666;
    font-weight: 500;
  }
`;

// 입찰단위 설정 관련 스타일
const BidUnitContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const BidUnitContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 8px;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const BidUnitActions = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e9ecef;
  flex-shrink: 0;
`;

const AddRangeButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: #f8f9fa;
  border: 1px dashed #97601a;
  border-radius: 8px;
  color: #97601a;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background-color: #fbf9f2;
    border-color: #97601a;
  }
`;

const RangesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const RangeItem = styled.div`
  padding: 20px;
  background-color: #fbf9f2;
  border: 1px solid #ddd8c7;
  border-radius: 8px;
  box-sizing: border-box;
`;

const RangeHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 12px;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const Label = styled.div`
  font-size: 15px;
  font-weight: 600;
  color: #97601a;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  box-sizing: border-box;
`;

const AmountGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UnitGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InputWrapper = styled.div`
  position: relative;
  flex: 1;
  min-width: 0;
`;

const InputLabel = styled.div`
  font-size: 13px;
  color: #887e67;
  margin-bottom: 4px;
`;

const Input = styled.input<{ readOnly?: boolean; hasError?: boolean }>`
  width: 100%;
  height: 36px;
  padding: 4px 40px 4px 12px;
  border: 1px solid ${props => props.hasError ? '#dc3545' : '#ddd8c7'};
  border-radius: 4px;
  font-size: 14px;
  color: #333;
  background-color: white;
  box-sizing: border-box;
  min-width: 0;

  &:focus {
    outline: none;
    border-color: ${props => props.hasError ? '#dc3545' : '#97601a'};
    box-shadow: 0 0 0 2px ${props => props.hasError ? 'rgba(220, 53, 69, 0.1)' : 'rgba(151, 96, 26, 0.1)'};
  }

  &::placeholder {
    color: #ddd8c7;
  }

  ${({ readOnly }) => readOnly && `
    background-color: #e9ecef;
    cursor: not-allowed;
  `}
`;

const Unit = styled.span`
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(15%);
  color: #887e67;
  font-size: 13px;
`;

const Separator = styled.div`
  font-size: 14px;
  color: #887e67;
  margin: 0 4px;
  padding-top: 28px;
  flex-shrink: 0;
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: #887e67;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  margin-left: auto;

  &:hover {
    background-color: rgba(136, 126, 103, 0.1);
    color: #97601a;
  }
`;

const ErrorMessage = styled.div`
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
`;

// 삭제 확인 다이얼로그 스타일 - Modal 컴포넌트 스타일 참고
const DeleteModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 20000; /* CmsPopup보다 높은 z-index */
  display: flex;
  justify-content: center;
  align-items: center;
  pointer-events: auto;
`;

const DeleteModalContainer = styled.div`
  position: fixed;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  
  background-color: #FFFFFF;
  padding: 24px 14px 12px 14px;
  border-radius: 14px;
  width: 300px;
  max-width: calc(100vw - 32px);
  min-width: 280px;
  z-index: 20001;
  
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const DeleteModalTitle = styled.div`
  text-align: center;
  font-size: 16px;
  font-weight: 760;
  color: #000000;
  margin-bottom: 16px;
  white-space: pre-line;
`;

const DeleteModalMessage = styled.div`
  font-size: 16px;
  line-height: 1.5;
  color: #000000;
  text-align: center;
  margin-bottom: 24px;
  flex-grow: 1;
  white-space: pre-line;
`;

const DeleteModalButtons = styled.div`
  display: flex;
  gap: 12px;
`;

interface DeleteModalButtonProps {
  variant: 'cancel' | 'delete';
}

const DeleteModalButton = styled.button<DeleteModalButtonProps>`
  border: none;
  font-weight: 500;
  transition: all 0.2s;
  cursor: pointer;
  border-radius: 8px;
  font-size: 1rem;
  padding: 0.75rem;
  flex: 1;
  
  ${props => props.variant === 'cancel' ? `
    background-color: transparent;
    border: 1px solid #6c757d;
    color: #6c757d;
    
    &:hover {
      background-color: #6c757d1A;
      color: #6c757d;
    }
  ` : `
    background-color: #dc3545;
    color: #FFFFFF;
    
    &:hover {
      background-color: #c82333;
    }
  `}
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    border-color: transparent;
  }
`;

// 인터페이스 정의
export interface AuctionFormData {
  auctioneerName: string;
  auctioneerId: string;
  auctionName: string;
  streamingUrl: string;
  auctionDetail: string;
  commissionRate: number;
  platformCommissionRate: number;
  preBidStartDate: Date | null;
  auctionStartDate: Date | null;
  auctionEndDate: Date | null;
  paymentDeadline: Date | null;
  hasPreBids?: boolean;
}

interface BidUnitRange {
  index: number;
  minAmount: string;
  maxAmount: string;
  unitAmount: string;
  confirmDate?: string;
  isNew?: boolean;
  delete?: boolean;
}

interface BiddingRate {
  index: number;
  minBidAmount: number;
  maxBidAmount: number | null;
  rate: number;
  delete: boolean;
}

export interface AuctionWithBidUnitData extends AuctionFormData {
  bidUnitRanges: BidUnitRange[];
  biddingRates?: BiddingRate[];
}

interface AuctionRegistrationWithBidUnitPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: AuctionWithBidUnitData) => void;
  onDelete?: () => void;
  initialData?: Partial<AuctionFormData>;
  initialBidUnitRanges?: BidUnitRange[];
  mode?: 'create' | 'edit';
}

export const AuctionRegistrationWithBidUnitPopup: React.FC<AuctionRegistrationWithBidUnitPopupProps> = ({
  isOpen,
  onClose,
  onSubmit,
  onDelete,
  initialData,
  initialBidUnitRanges,
  mode = 'create'
}) => {
  // 경매 폼 데이터
  const [formData, setFormData] = useState<AuctionFormData>({
    auctioneerName: initialData?.auctioneerName || '',
    auctioneerId: initialData?.auctioneerId || '',
    auctionName: initialData?.auctionName || '',
    streamingUrl: initialData?.streamingUrl || '',
    auctionDetail: initialData?.auctionDetail || '',
    commissionRate: initialData?.commissionRate ?? 0,
    platformCommissionRate: initialData?.platformCommissionRate ?? 0,
    preBidStartDate: initialData?.preBidStartDate || null,
    auctionStartDate: initialData?.auctionStartDate || null,
    auctionEndDate: initialData?.auctionEndDate || null,
    paymentDeadline: initialData?.paymentDeadline || null,
    hasPreBids: initialData?.hasPreBids || false,
  });

  // 입찰단위 데이터
  const [bidUnitRanges, setBidUnitRanges] = useState<BidUnitRange[]>(() => {
    if (initialBidUnitRanges && initialBidUnitRanges.length > 0) {
      return initialBidUnitRanges.map(range => ({
        ...range,
        delete: range.delete ?? false
      }));
    }
    return [
      {
        index: 1,
        minAmount: "1",
        maxAmount: "100000",
        unitAmount: "1000",
        delete: false,
      },
      {
        index: 2,
        minAmount: "100001",
        maxAmount: "",
        unitAmount: "10000",
        delete: false,
      },
    ];
  });

  const [errors, setErrors] = useState<Partial<Record<keyof AuctionFormData | 'bidUnitRanges', string>>>({});
  const [bidUnitErrors, setBidUnitErrors] = useState<{[key: number]: {[field: string]: string}}>({});

  // 경매사 검색 팝업 상태
  const [isAuctioneerPopupOpen, setIsAuctioneerPopupOpen] = useState(false);

  // 삭제 확인 모달 상태
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  // 팝업이 열릴 때 폼 초기화
  useEffect(() => {
    if (isOpen) {
      // 에러 상태 초기화
      setErrors({});
      setBidUnitErrors({});
      
      if (initialData && Object.keys(initialData).length > 0) {
        // initialData가 있는 경우 (수정 모드)
        setFormData({
          auctioneerName: initialData.auctioneerName || '',
          auctioneerId: initialData.auctioneerId || '',
          auctionName: initialData.auctionName || '',
          streamingUrl: initialData.streamingUrl || '',
          auctionDetail: initialData.auctionDetail || '',
          commissionRate: initialData.commissionRate ?? 0,
          platformCommissionRate: initialData.platformCommissionRate ?? 0,
          preBidStartDate: initialData.preBidStartDate || null,
          auctionStartDate: initialData.auctionStartDate || null,
          auctionEndDate: initialData.auctionEndDate || null,
          paymentDeadline: initialData.paymentDeadline || null,
          hasPreBids: initialData.hasPreBids || false,
        });
        
        // 경매사 이름은 표시용으로만 사용
      } else {
        // initialData가 없는 경우 (생성 모드)
        setFormData({
          auctioneerName: '',
          auctioneerId: '',
          auctionName: '',
          streamingUrl: '',
          auctionDetail: '',
          commissionRate: 0,
          platformCommissionRate: 0,
          preBidStartDate: null,
          auctionStartDate: null,
          auctionEndDate: null,
          paymentDeadline: null,
          hasPreBids: false,
        });
      }
    }
  }, [isOpen, initialData]);

  // 팝업이 열릴 때 입찰단위 초기화
  useEffect(() => {
    if (isOpen) {
      if (initialBidUnitRanges && initialBidUnitRanges.length > 0) {
        // initialBidUnitRanges가 있는 경우 (수정 모드)
        setBidUnitRanges(initialBidUnitRanges.map(range => ({
          ...range,
          delete: range.delete ?? false
        })));
      } else {
        // initialBidUnitRanges가 없는 경우 (생성 모드)
        setBidUnitRanges([
          {
            index: 1,
            minAmount: "1",
            maxAmount: "100000",
            unitAmount: "1000",
            delete: false,
          },
          {
            index: 2,
            minAmount: "100001",
            maxAmount: "",
            unitAmount: "10000",
            delete: false,
          },
        ]);
      }
    }
  }, [isOpen, initialBidUnitRanges]);

  // 경매사 선택 핸들러
  const handleAuctioneerSelect = (auctioneer: any) => {
    setFormData(prev => ({
      ...prev,
      auctioneerId: auctioneer.index.toString(),
      auctioneerName: auctioneer.name
    }));
    
    if (errors.auctioneerName) {
      setErrors(prev => ({
        ...prev,
        auctioneerName: undefined
      }));
    }
    
    setIsAuctioneerPopupOpen(false);
  };

  // 경매사 선택 해제 핸들러
  const handleAuctioneerClear = () => {
    setFormData(prev => ({
      ...prev,
      auctioneerId: '',
      auctioneerName: ''
    }));
  };

  // 경매 폼 입력 변경 핸들러
  const handleInputChange = (field: keyof AuctionFormData, value: string | number | Date | null) => {
    let newFormData = {
      ...formData,
      [field]: value
    };
    let wasAutoAdjusted = false;
    let autoAdjustMessage = '';

    // 날짜 필드의 경우 기본 검증만 수행 (시간 제한은 UI에서 처리)
    if (field === 'preBidStartDate' && value instanceof Date) {
      const now = new Date();
      if (value <= now) {
        // 현재 시간보다 느리거나 같은 경우 1분 후로 설정
        value = new Date(now.getTime() + 60000);
        wasAutoAdjusted = true;
        autoAdjustMessage = '사전입찰 시작일시는 현재 시각 이후여야 하므로 1분 후로 조정되었습니다.';
      }
    }
    
    if (field === 'paymentDeadline' && value instanceof Date) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (value < today) {
        value = today;
        wasAutoAdjusted = true;
        autoAdjustMessage = '결제 마감일은 오늘 이후여야 하므로 오늘로 조정되었습니다.';
      }
    }

    newFormData = {
      ...newFormData,
      [field]: value
    };

    setFormData(newFormData);

    if (wasAutoAdjusted && autoAdjustMessage) {
      toast.warning(autoAdjustMessage);
    }

    // 에러 메시지 클리어
    const fieldsTooClear = [field];
    
    if (field === 'preBidStartDate') {
      fieldsTooClear.push('auctionStartDate', 'auctionEndDate', 'paymentDeadline');
    } else if (field === 'auctionStartDate') {
      fieldsTooClear.push('preBidStartDate', 'auctionEndDate', 'paymentDeadline');
    } else if (field === 'auctionEndDate') {
      fieldsTooClear.push('auctionStartDate', 'preBidStartDate', 'paymentDeadline');
    } else if (field === 'paymentDeadline') {
      fieldsTooClear.push('auctionEndDate', 'auctionStartDate', 'preBidStartDate');
    }
    
    setErrors(prev => {
      const newErrors = { ...prev };
      fieldsTooClear.forEach(fieldName => {
        if (newErrors[fieldName as keyof AuctionFormData]) {
          delete newErrors[fieldName as keyof AuctionFormData];
        }
      });
      return newErrors;
    });
  };

  // 입찰단위 값 변경 핸들러
  const handleBidUnitValueChange = (index: number, field: keyof BidUnitRange, value: string) => {
    if (field === "minAmount" || field === "maxAmount" || field === "unitAmount") {
      const numericValue = value.toString().replace(/[^0-9]/g, "");
      const limitedValue = numericValue.slice(0, 10);
      
      setBidUnitRanges((ranges) => {
        const updatedRanges = [...ranges];
        const currentRangeIndex = updatedRanges.findIndex(r => r.index === index);
        
        if (currentRangeIndex >= 0) {
          updatedRanges[currentRangeIndex] = {
            ...updatedRanges[currentRangeIndex],
            [field]: limitedValue
          };
        }
        
        // 구간 간 연동 로직 (삭제된 구간 제외)
        const visibleRanges = updatedRanges.filter(r => !r.delete);
        const currentVisibleIndex = visibleRanges.findIndex(r => r.index === index);
        
        if (field === "maxAmount" && currentVisibleIndex >= 0) {
          const nextVisibleIndex = currentVisibleIndex + 1;
          if (nextVisibleIndex < visibleRanges.length) {
            const nextRange = visibleRanges[nextVisibleIndex];
            const nextRangeIndex = updatedRanges.findIndex(r => r.index === nextRange.index);
            const newMinAmount = limitedValue ? (Number(limitedValue) + 1).toString() : "";
            updatedRanges[nextRangeIndex] = {
              ...updatedRanges[nextRangeIndex],
              minAmount: newMinAmount
            };
          }
        } else if (field === "minAmount" && currentVisibleIndex > 0) {
          const prevVisibleIndex = currentVisibleIndex - 1;
          const prevRange = visibleRanges[prevVisibleIndex];
          const prevRangeIndex = updatedRanges.findIndex(r => r.index === prevRange.index);
          const newMaxAmount = limitedValue ? (Number(limitedValue) - 1).toString() : "";
          updatedRanges[prevRangeIndex] = {
            ...updatedRanges[prevRangeIndex],
            maxAmount: newMaxAmount
          };
        }
        
        return updatedRanges;
      });

      // 해당 필드의 에러 클리어
      setBidUnitErrors(prev => {
        const newErrors = { ...prev };
        if (newErrors[index] && newErrors[index][field]) {
          delete newErrors[index][field];
          if (Object.keys(newErrors[index]).length === 0) {
            delete newErrors[index];
          }
        }
        return newErrors;
      });
    }
  };

  // 입찰단위 구간 추가
  const handleAddBidUnitRange = () => {
    const visibleRanges = bidUnitRanges.filter(range => !range.delete);
    const newIndex = Math.max(...bidUnitRanges.map(r => r.index)) + 1;
    const secondLastRange = visibleRanges[visibleRanges.length - 2];
    
    const newMinAmount = secondLastRange?.maxAmount ? 
      (Number(secondLastRange.maxAmount) + 1).toString() : "";
    
    let rangeSize = 100000;
    if (secondLastRange?.maxAmount && secondLastRange?.minAmount) {
      rangeSize = Number(secondLastRange.maxAmount) - Number(secondLastRange.minAmount) + 1;
      rangeSize = Math.max(rangeSize, 10000);
    }
    
    const newRange = {
      index: newIndex,
      minAmount: newMinAmount,
      maxAmount: newMinAmount ? (Number(newMinAmount) + rangeSize - 1).toString() : "",
      unitAmount: "5000",
      delete: false,
    };
    
    const updatedRanges = [...bidUnitRanges];
    
    // 마지막 구간 이전에 새 구간을 삽입 (splice를 사용해서 마지막에서 두번째 위치에 삽입)
    const lastVisibleRangeIndex = updatedRanges.findIndex(r => !r.delete && r.maxAmount === "");
    if (lastVisibleRangeIndex >= 0) {
      updatedRanges.splice(lastVisibleRangeIndex, 0, newRange);
      
      // 새로 삽입된 구간 다음에 있는 마지막 구간(이상 구간)의 minAmount 업데이트
      if (newRange.maxAmount) {
        updatedRanges[lastVisibleRangeIndex + 1] = {
          ...updatedRanges[lastVisibleRangeIndex + 1],
          minAmount: (Number(newRange.maxAmount) + 1).toString()
        };
      }
    } else {
      // 마지막 구간을 찾을 수 없는 경우 배열 끝에 추가
      updatedRanges.push(newRange);
    }
    
    setBidUnitRanges(updatedRanges);
  };

  // 입찰단위 구간 삭제 (마지막 구간 삭제)
  const handleDeleteBidUnitRange = () => {
    const visibleRanges = bidUnitRanges.filter(range => !range.delete);
    if (visibleRanges.length > 2) {
      const lastVisibleRange = visibleRanges[visibleRanges.length - 1];
      setBidUnitRanges((ranges) => {
        const updatedRanges = ranges.map(range => 
          range.index === lastVisibleRange.index 
            ? { ...range, delete: true }
            : range
        );
        
        // 새로운 마지막 구간의 maxAmount를 빈 값으로 설정 (이상 구간으로 만들기)
        const newVisibleRanges = updatedRanges.filter(range => !range.delete);
        if (newVisibleRanges.length > 0) {
          const sortedRanges = [...newVisibleRanges].sort((a, b) => Number(a.minAmount) - Number(b.minAmount));
          const newLastRange = sortedRanges[sortedRanges.length - 1];
          const newLastRangeIndex = updatedRanges.findIndex(r => r.index === newLastRange.index);
          if (newLastRangeIndex >= 0) {
            updatedRanges[newLastRangeIndex] = {
              ...updatedRanges[newLastRangeIndex],
              maxAmount: ""
            };
          }
        }
        
        return updatedRanges;
      });
    }
  };

  // 개별 입찰단위 구간 삭제
  const handleDeleteSpecificRange = (indexToDelete: number) => {
    const visibleRanges = bidUnitRanges.filter(range => !range.delete);
    if (visibleRanges.length <= 2) {
      return; // 최소 2구간 유지
    }

    setBidUnitRanges((ranges) => {
      const updatedRanges = ranges.map(range => 
        range.index === indexToDelete 
          ? { ...range, delete: true }
          : range
      );

      // 삭제되지 않은 구간들만 필터링하여 UI 표시용으로 사용
      const newVisibleRanges = updatedRanges.filter(range => !range.delete);
      
      // 구간 간 연결성 유지 - 삭제된 구간의 영향을 받는 이전/이후 구간 조정
      const sortedVisibleRanges = [...newVisibleRanges].sort((a, b) => Number(a.minAmount) - Number(b.minAmount));
      
      // 마지막 구간의 maxAmount를 빈 값으로 설정 (이상 구간으로 만들기)
      if (sortedVisibleRanges.length > 0) {
        const lastVisibleRange = sortedVisibleRanges[sortedVisibleRanges.length - 1];
        const lastRangeIndex = updatedRanges.findIndex(r => r.index === lastVisibleRange.index);
        if (lastRangeIndex >= 0) {
          updatedRanges[lastRangeIndex] = {
            ...updatedRanges[lastRangeIndex],
            maxAmount: ""
          };
        }
      }
      
      // 연결성 유지를 위한 구간 조정
      for (let i = 0; i < sortedVisibleRanges.length - 1; i++) {
        const currentRange = sortedVisibleRanges[i];
        const nextRange = sortedVisibleRanges[i + 1];
        
        // 현재 구간의 maxAmount와 다음 구간의 minAmount를 연결
        if (currentRange.maxAmount && Number(currentRange.maxAmount) + 1 !== Number(nextRange.minAmount)) {
          const nextIndex = updatedRanges.findIndex(r => r.index === nextRange.index);
          
          if (nextIndex >= 0) {
            updatedRanges[nextIndex] = {
              ...updatedRanges[nextIndex],
              minAmount: (Number(currentRange.maxAmount) + 1).toString()
            };
          }
        }
      }
      
      return updatedRanges;
    });
  };

  const formatNumber = (value: string) => {
    const number = value.replace(/[^0-9]/g, "");
    return number ? Number(number).toLocaleString() : "";
  };

  // 입찰단위 유효성 검사
  const validateBidUnitRange = (currentRange: BidUnitRange) => {
    const minAmount = Number(currentRange.minAmount);
    const maxAmount = Number(currentRange.maxAmount);
    const unitAmount = Number(currentRange.unitAmount);

    const errors: {[field: string]: string} = {};

    if (minAmount < 0) {
      errors.minAmount = "금액은 0 이상이어야 합니다.";
    }

    // maxAmount가 비어있지 않은 경우에만 검증 (마지막 구간은 maxAmount가 비어있음)
    if (currentRange.maxAmount !== "") {
      if (maxAmount < 0) {
        errors.maxAmount = "금액은 0 이상이어야 합니다.";
      }
      if (minAmount >= maxAmount) {
        errors.maxAmount = "최소 금액은 최대 금액보다 작아야 합니다.";
      }
    }

    if (unitAmount <= 0) {
      errors.unitAmount = "입찰단위는 0보다 커야 합니다.";
    }

    return errors;
  };

  // 전체 폼 유효성 검사
  const validateForm = (): boolean => {
    const newErrors: Partial<Record<keyof AuctionFormData | 'bidUnitRanges', string>> = {};
    const newBidUnitErrors: {[key: number]: {[field: string]: string}} = {};
    const now = new Date();
    const isPreBidAlreadyStarted = !!(formData.preBidStartDate && formData.preBidStartDate <= now);
    const minAuctionStartFromNow = new Date(now.getTime() + 30 * 60 * 1000);

    // 경매 폼 유효성 검사
    if (!formData.auctioneerName.trim()) {
      newErrors.auctioneerName = '경매사를 선택해주세요.';
    }

    if (!formData.auctionName.trim()) {
      newErrors.auctionName = '경매명을 입력해주세요.';
    }

    if (!formData.streamingUrl.trim()) {
      newErrors.streamingUrl = '스트리밍 URL을 입력해주세요.';
    } else {
      const urlPattern = /^https?:\/\/.+/;
      if (!urlPattern.test(formData.streamingUrl)) {
        newErrors.streamingUrl = '올바른 URL 형식을 입력해주세요 (http:// 또는 https://)';
      }
    }

    if (!formData.auctionDetail.trim()) {
      newErrors.auctionDetail = '경매 상세 내용을 입력해주세요.';
    }

    if (isNaN(formData.commissionRate) || formData.commissionRate === null || formData.commissionRate === undefined) {
      newErrors.commissionRate = '경매 수수료를 입력해주세요.';
    } else if (formData.commissionRate < 0 || formData.commissionRate > 100) {
      newErrors.commissionRate = '수수료는 0~100% 사이여야 합니다.';
    }

    if (isNaN(formData.platformCommissionRate) || formData.platformCommissionRate === null || formData.platformCommissionRate === undefined) {
      newErrors.platformCommissionRate = '플랫폼 수수료를 입력해주세요.';
    } else if (formData.platformCommissionRate < 0 || formData.platformCommissionRate > 100) {
      newErrors.platformCommissionRate = '플랫폼 수수료는 0~100% 사이여야 합니다.';
    }

    // 날짜 필드 검증
    if (!formData.preBidStartDate) {
      newErrors.preBidStartDate = '사전입찰 시작일시를 입력해주세요.';
    } else if (!isPreBidAlreadyStarted && formData.preBidStartDate < now) {
      // 사전입찰이 아직 시작 전인 경우에만 현재 시각 이후 여부를 검증
      newErrors.preBidStartDate = '사전입찰 시작일시는 현재 시각 이후여야 합니다.';
    }

    if (!formData.auctionStartDate) {
      newErrors.auctionStartDate = '경매 시작일시를 입력해주세요.';
    } else if (isPreBidAlreadyStarted) {
      // 사전입찰이 이미 시작된 경우: 현재 시각 기준 최소 30분 이후여야 함
      if (formData.auctionStartDate < minAuctionStartFromNow) {
        newErrors.auctionStartDate = '사전입찰 시작 이후에는 경매 시작일시는 현재 시각으로부터 최소 30분 이후여야 합니다.';
      }
    } else if (formData.auctionStartDate < now) {
      // 사전입찰 시작 전에는 기존처럼 "현재 시각 이후"만 검증
      newErrors.auctionStartDate = '경매 시작일시는 현재 시각 이후여야 합니다.';
    }

    if (!formData.auctionEndDate) {
      newErrors.auctionEndDate = '경매 종료일시를 입력해주세요.';
    } else if (formData.auctionEndDate < now) {
      newErrors.auctionEndDate = '경매 종료일시는 현재 시각 이후여야 합니다.';
    }

    if (!formData.paymentDeadline) {
      newErrors.paymentDeadline = '결제 마감일을 입력해주세요.';
    } else if (formData.paymentDeadline < now) {
      newErrors.paymentDeadline = '결제 마감일은 오늘 이후여야 합니다.';
    }

    // 날짜 유효성 검증
    if (formData.preBidStartDate && formData.auctionStartDate && 
        formData.preBidStartDate >= formData.auctionStartDate) {
      newErrors.preBidStartDate = '사전입찰 시작일시는 경매 시작일시보다 빨라야 합니다.';
      newErrors.auctionStartDate = '경매 시작일시는 사전입찰 시작일시보다 늦어야 합니다.';
    }

    if (formData.auctionStartDate && formData.auctionEndDate && 
        formData.auctionStartDate >= formData.auctionEndDate) {
      newErrors.auctionStartDate = '경매 시작일시는 경매 종료일시보다 빨라야 합니다.';
      newErrors.auctionEndDate = '경매 종료일시는 경매 시작일시보다 늦어야 합니다.';
    }

    if (formData.auctionEndDate && formData.paymentDeadline) {
      const auctionEndDateOnly = new Date(formData.auctionEndDate);
      auctionEndDateOnly.setHours(23, 59, 59, 999);
      
      const paymentDeadlineOnly = new Date(formData.paymentDeadline);
      paymentDeadlineOnly.setHours(0, 0, 0, 0);
      
      if (auctionEndDateOnly >= paymentDeadlineOnly) {
        newErrors.auctionEndDate = '경매 종료일시는 결제 마감일보다 빨라야 합니다.';
        newErrors.paymentDeadline = '결제 마감일은 경매 종료일 다음 날 이후여야 합니다.';
      }
    }

    // 입찰단위 유효성 검사 (삭제된 구간 제외)
    let hasBidUnitError = false;
    const visibleRanges = bidUnitRanges.filter(range => !range.delete);
    for (const range of visibleRanges) {
      const rangeErrors = validateBidUnitRange(range);
      if (Object.keys(rangeErrors).length > 0) {
        newBidUnitErrors[range.index] = rangeErrors;
        hasBidUnitError = true;
      }
    }

    // 구간 간 연결성 검사 (삭제된 구간 제외)
    const sortedRanges = [...visibleRanges].sort((a, b) => Number(a.minAmount) - Number(b.minAmount));
    
    for (let i = 0; i < sortedRanges.length - 1; i++) {
      const currentRange = sortedRanges[i];
      const nextRange = sortedRanges[i + 1];
      
      const currentMax = Number(currentRange.maxAmount);
      const nextMin = Number(nextRange.minAmount);
      
      if (currentMax + 1 !== nextMin) {
        newErrors.bidUnitRanges = `구간이 연결되지 않습니다. ${i + 1}번째와 ${i + 2}번째 구간을 확인해주세요.`;
        hasBidUnitError = true;
        break;
      }
    }

    // 전 구간의 입찰단위가 다음 구간의 입찰단위보다 큰지 검사 (삭제된 구간 제외)
    for (let i = 0; i < sortedRanges.length - 1; i++) {
      const currentRange = sortedRanges[i];
      const nextRange = sortedRanges[i + 1];
      
      const currentUnitAmount = Number(currentRange.unitAmount);
      const nextUnitAmount = Number(nextRange.unitAmount);
      
      if (currentUnitAmount > nextUnitAmount) {
        newErrors.bidUnitRanges = `${i + 1}번째 구간의 입찰단위(${currentUnitAmount.toLocaleString()}원)가 ${i + 2}번째 구간의 입찰단위(${nextUnitAmount.toLocaleString()}원)보다 큽니다. 구간별 입찰단위는 감소하지 않아야 합니다.`;
        hasBidUnitError = true;
        break;
      }
    }

    // 첫 번째 구간이 1부터 시작하는지 확인 (삭제된 구간 제외)
    if (sortedRanges.length > 0 && Number(sortedRanges[0].minAmount) !== 1) {
      newErrors.bidUnitRanges = '첫 번째 구간은 1원부터 시작해야 합니다.';
      hasBidUnitError = true;
    }

    if (hasBidUnitError && !newErrors.bidUnitRanges) {
      newErrors.bidUnitRanges = '입찰단위 설정에 오류가 있습니다.';
    }

    setErrors(newErrors);
    setBidUnitErrors(newBidUnitErrors);
    
    return Object.keys(newErrors).length === 0 && Object.keys(newBidUnitErrors).length === 0;
  };

  // 제출 핸들러
  const handleSubmit = () => {
    if (validateForm()) {
      // bidUnitRanges를 biddingRates 형태로 변환하여 전송
      const biddingRates = bidUnitRanges.map((range, index) => {
        const isLastRange = index === bidUnitRanges.length - 1;
        const biddingRate: any = {
          index: range.index,
          minBidAmount: Number(range.minAmount),
          rate: Number(range.unitAmount),
          delete: range.delete ?? false,
        };
        
        // 마지막 구간이 아닌 경우에만 maxBidAmount 포함
        if (!isLastRange) {
          biddingRate.maxBidAmount = Number(range.maxAmount);
        }
        
        return biddingRate;
      });

      const submitData: AuctionWithBidUnitData = {
        ...formData,
        bidUnitRanges,
        biddingRates, // API 전송용 데이터 추가
      };
      onSubmit(submitData);
      onClose();
    }
  };

  // 취소 핸들러
  const handleCancel = () => {
    onClose();
  };

  // 삭제 확인 핸들러
  const handleDeleteConfirm = () => {
    setIsDeleteModalOpen(false);
    if (onDelete) {
      onDelete();
    }
  };

  const handleDeleteCancel = () => {
    setIsDeleteModalOpen(false);
  };

  // 삭제 모달 ESC 키 핸들러
  useEffect(() => {
    if (isDeleteModalOpen) {
      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          event.stopPropagation(); // 상위 컴포넌트로 이벤트 전파 방지
          setIsDeleteModalOpen(false);
        }
      };

      document.addEventListener('keydown', handleKeyDown, true); // capture phase에서 처리
      
      return () => {
        document.removeEventListener('keydown', handleKeyDown, true);
      };
    }
  }, [isDeleteModalOpen]);

  const hasPreBids = formData.hasPreBids;
  const currentTime = new Date();
  
  // 각 날짜가 현재 시간을 지났는지 확인
  const isPreBidStartPassed = !!(formData.preBidStartDate && formData.preBidStartDate < currentTime);
  const isAuctionStartPassed = !!(formData.auctionStartDate && formData.auctionStartDate < currentTime);

  // 사전입찰 시작 이후 경매 시작일시의 최소 선택 가능 시간 (현재 시각 기준 30분 확보)
  const minAuctionStartDate = (() => {
    if (isPreBidStartPassed) {
      return new Date(currentTime.getTime() + 30 * 60 * 1000);
    }
    if (formData.preBidStartDate) {
      return new Date(formData.preBidStartDate.getTime() + 60000);
    }
    return currentTime;
  })();
  
  // 수정 제한 조건들
  // 1. 사전입찰자가 있으면 모든 것 수정 불가
  // 2. 경매 시작 시간이 지났으면 모든 것 수정 불가
  // 3. 사전입찰 시작 이후지만 사전입찰자가 없을 경우에는 경매명과 경매 상세 내용만 수정 가능
  const isAllFieldsLocked = hasPreBids || isAuctionStartPassed;
  const isPartialEditMode = !hasPreBids && isPreBidStartPassed && !isAuctionStartPassed;
  const isPreBidFieldLocked = (isAllFieldsLocked || isPreBidStartPassed) && mode === 'edit';
  
  const rightButtons = [
    ...(hasPreBids && mode === 'edit' ? [] : [
      {
        label: '저장',
        onClick: handleSubmit,
        type: CmsPopupBtnType.PRIMARY,
      }
    ]),
    {
      label: hasPreBids && mode === 'edit' ? '확인' : '닫기',
      onClick: handleCancel,
      type: CmsPopupBtnType.SECONDARY,
    },
  ];

  const leftButtons = (mode === 'edit' && onDelete && !isAllFieldsLocked) ? [
    {
      label: '삭제',
      onClick: () => setIsDeleteModalOpen(true),
      type: CmsPopupBtnType.PRIMARY,
    },
  ] : [];

  return (
    <CmsPopup
      isOpen={isOpen}
      onClose={onClose}
      width={"1200px"}
      height={"750px"}
      showRequiredMark={true}
      rightButtons={rightButtons}
      leftButtons={leftButtons}
    >
      <PopupContainer>
        <SectionTitleRow>
          <SectionTitle>경매 등록 및 입찰단위 설정</SectionTitle>
          <RequiredIndicator>*필수항목</RequiredIndicator>
        </SectionTitleRow>
        
        <MainContainer>
          {/* 왼쪽 패널 - 경매 등록 */}
          <LeftPanel>
            <SectionTitle style={{ marginBottom: '24px', fontSize: '16px' }}>경매 등록상세</SectionTitle>
            
            {/* 수정 제한 안내 메시지 표시 */}
            {(isAllFieldsLocked || isPartialEditMode) && mode === 'edit' && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#fff3cd',
                border: '1px solid #ffeaa7',
                borderRadius: '8px',
                marginBottom: '20px',
                color: '#856404',
                fontSize: '14px',
              }}>
                {hasPreBids && '⚠️ 이 경매에는 사전입찰이 등록되어 있어 수정 및 삭제가 제한됩니다.'}
                {!hasPreBids && isAuctionStartPassed && '⚠️ 경매가 시작되어 수정 및 삭제가 제한됩니다.'}
                {!hasPreBids && isPartialEditMode && '⚠️ 사전입찰이 시작되어 사전입찰 시작일시는 수정할 수 없으며, 경매 시작일시는 현재 시각으로부터 최소 30분 이후로만 설정 가능합니다.'}
              </div>
            )}

            {/* 경매 일정 섹션 */}
            <Section>
              <SubSectionTitle>경매 일정</SubSectionTitle>
              
              <FormGroup>
                <AppDateTimePicker
                  label="*사전입찰 시작일시"
                  value={formData.preBidStartDate}
                  onChange={isPreBidFieldLocked ? () => {} : (date) => handleInputChange('preBidStartDate', date)}
                  showTime={true}
                  errorMessage={errors.preBidStartDate}
                  minDate={new Date()}
                  maxDate={formData.auctionStartDate ? new Date(formData.auctionStartDate.getTime() - 60000) : undefined}
                  disabled={isPreBidFieldLocked}
                />
              </FormGroup>

              <DateRangeRow>
                <DateFieldWrapper>
                  <AppDateTimePicker
                    label="*경매 시작일시"
                    value={formData.auctionStartDate}
                    onChange={isAllFieldsLocked && mode === 'edit' ? () => {} : (date) => handleInputChange('auctionStartDate', date)}
                    showTime={true}
                    errorMessage={errors.auctionStartDate}
                    minDate={minAuctionStartDate}
                    maxDate={formData.auctionEndDate ? new Date(formData.auctionEndDate.getTime() - 60000) : undefined}
                    disabled={!!(isAllFieldsLocked && mode === 'edit')}
                  />
                </DateFieldWrapper>
                <DateSeparator>
                  <span>~</span>
                </DateSeparator>
                <DateFieldWrapper>
                  <AppDateTimePicker
                    label="*경매 종료일시"
                    value={formData.auctionEndDate}
                    onChange={isAllFieldsLocked && mode === 'edit' ? () => {} : (date) => handleInputChange('auctionEndDate', date)}
                    showTime={true}
                    errorMessage={errors.auctionEndDate}
                    minDate={formData.auctionStartDate ? new Date(formData.auctionStartDate.getTime() + 60000) : new Date()}
                    maxDate={formData.paymentDeadline ? (() => {
                      const paymentDate = new Date(formData.paymentDeadline);
                      paymentDate.setHours(23, 59, 59, 999);
                      return new Date(paymentDate.getTime() - 1);
                    })() : undefined}
                    disabled={!!(isAllFieldsLocked && mode === 'edit')}
                  />
                </DateFieldWrapper>
              </DateRangeRow>

              <FormGroup>
                <AppDateTimePicker
                  label="*결제 마감일"
                  value={formData.paymentDeadline}
                  onChange={isAllFieldsLocked && mode === 'edit' ? () => {} : (date) => handleInputChange('paymentDeadline', date)}
                  showTime={false}
                  errorMessage={errors.paymentDeadline}
                  minDate={formData.auctionEndDate ? (() => {
                    const nextDay = new Date(formData.auctionEndDate);
                    nextDay.setDate(nextDay.getDate() + 1);
                    nextDay.setHours(0, 0, 0, 0);
                    return nextDay;
                  })() : new Date()}
                  disabled={!!(isAllFieldsLocked && mode === 'edit')}
                />
              </FormGroup>
            </Section>

            {/* 경매 정보 섹션 */}
            <Section>
              <SubSectionTitle>경매 정보</SubSectionTitle>
              
              <FormGroup>
                <div 
                  onClick={() => !(isAllFieldsLocked && mode === 'edit') && setIsAuctioneerPopupOpen(true)} 
                  style={{ 
                    cursor: isAllFieldsLocked && mode === 'edit' ? 'default' : 'pointer',
                    position: 'relative'
                  }}
                >
                  <AppTextField 
                    label="*경매사 검색" 
                    value={formData.auctioneerName}
                    onChange={() => {}} // 읽기 전용이므로 빈 함수
                    placeholder={isAllFieldsLocked && mode === 'edit' ? "수정할 수 없습니다" : "경매사를 선택하세요"}
                    errorMessage={errors.auctioneerName}
                    readOnly={true}
                  />
                  {formData.auctioneerName && !(isAllFieldsLocked && mode === 'edit') && (
                    <IconButton
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        handleAuctioneerClear();
                      }}
                      size="small"
                      style={{ 
                        position: 'absolute', 
                        right: '8px', 
                        top: '50%', 
                        transform: 'translateY(-50%)',
                        zIndex: 1
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  )}
                </div>
              </FormGroup>

              <FormGroup>
                <AppUrlTextField
                  label="*경매 스트리밍 URL"
                  value={formData.streamingUrl}
                  onChange={isAllFieldsLocked && mode === 'edit' ? () => {} : (e) => handleInputChange('streamingUrl', e.target.value)}
                  placeholder={isAllFieldsLocked && mode === 'edit' ? "수정할 수 없습니다" : "스트리밍 URL을 입력하세요 (예: https://stream.example.com)"}
                  errorMessage={errors.streamingUrl}
                  readOnly={!!(isAllFieldsLocked && mode === 'edit')}
                />
              </FormGroup>

              <FormGroup>
                <AppTextField
                  label="*경매명"
                  value={formData.auctionName}
                  onChange={isAllFieldsLocked && mode === 'edit' ? () => {} : (e) => handleInputChange('auctionName', e.target.value)}
                  placeholder={isAllFieldsLocked && mode === 'edit' ? "수정할 수 없습니다" : "경매명을 입력하세요"}
                  errorMessage={errors.auctionName}
                  readOnly={!!(isAllFieldsLocked && mode === 'edit')}
                />
              </FormGroup>

              <FormGroup>
                <AppTextField
                  label="*경매 상세 내용"
                  value={formData.auctionDetail}
                  onChange={isAllFieldsLocked && mode === 'edit' ? () => {} : (e) => handleInputChange('auctionDetail', e.target.value)}
                  placeholder={isAllFieldsLocked && mode === 'edit' ? "수정할 수 없습니다" : "경매에 대한 상세설명을 입력하세요"}
                  multiline
                  minLines={5}
                  height='150px'
                  errorMessage={errors.auctionDetail}
                  readOnly={!!(isAllFieldsLocked && mode === 'edit')}
                />
              </FormGroup>
            </Section>

            {/* 수수료 설정 섹션 */}
            <Section>
              <SubSectionTitle>수수료 설정</SubSectionTitle>
              
              <FormGroup>
                <AppNumberTextField
                  label="*경매 수수료 요율"
                  value={formData.commissionRate}
                  onChange={isAllFieldsLocked && mode === 'edit' ? () => {} : (e) => handleInputChange('commissionRate', parseFloat(e.target.value) || 0)}
                  placeholder={isAllFieldsLocked && mode === 'edit' ? "수정할 수 없습니다" : ""}
                  errorMessage={errors.commissionRate}
                  maxValue={100}
                  minValue={0}
                  allowDecimal={true}
                  decimalPlaces={2}
                  unit="%"
                  readOnly={!!(isAllFieldsLocked && mode === 'edit')}
                />
              </FormGroup>

              <FormGroup>
                <AppNumberTextField
                  label="*플랫폼 수수료 요율"
                  value={formData.platformCommissionRate}
                  onChange={isAllFieldsLocked && mode === 'edit' ? () => {} : (e) => handleInputChange('platformCommissionRate', parseFloat(e.target.value) || 0)}
                  placeholder={isAllFieldsLocked && mode === 'edit' ? "수정할 수 없습니다" : ""}
                  errorMessage={errors.platformCommissionRate}
                  maxValue={100}
                  minValue={0}
                  allowDecimal={true}
                  decimalPlaces={2}
                  unit="%"
                  readOnly={!!(isAllFieldsLocked && mode === 'edit')}
                />
              </FormGroup>
            </Section>
          </LeftPanel>

          {/* 오른쪽 패널 - 입찰단위 설정 */}
          <RightPanel>
            <BidUnitContainer>
              <SectionTitle style={{ marginBottom: '24px', fontSize: '16px' }}>*입찰단위 설정</SectionTitle>
              
              {errors.bidUnitRanges && (
                <ErrorMessage style={{ marginBottom: '16px' }}>
                  {errors.bidUnitRanges}
                </ErrorMessage>
              )}

              <BidUnitContent>
                <RangesContainer>
                  {bidUnitRanges.filter(range => !range.delete).map((range, idx) => {
                    const visibleRanges = bidUnitRanges.filter(r => !r.delete);
                    const isLastRange = idx === visibleRanges.length - 1;
                    const rangeErrors = bidUnitErrors[range.index] || {};
                    
                    return (
                      <RangeItem key={range.index}>
                        <RangeHeader>
                          <HeaderLeft>
                            <Label>구간 {idx + 1}</Label>
                          </HeaderLeft>
                          {/* 마지막 구간 삭제 버튼 (기존) */}
                          {bidUnitRanges.filter(r => !r.delete).length > 2 && isLastRange && !isAllFieldsLocked && (
                            <DeleteButton onClick={handleDeleteBidUnitRange}>
                              ✕
                            </DeleteButton>
                          )}
                          {/* 개별 구간 삭제 버튼 (첫번째 구간 제외) */}
                          {bidUnitRanges.filter(r => !r.delete).length > 2 && !isLastRange && idx !== 0 && !isAllFieldsLocked && (
                            <DeleteButton onClick={() => handleDeleteSpecificRange(range.index)}>
                              ✕
                            </DeleteButton>
                          )}
                        </RangeHeader>
                        <InputGroup>
                          <AmountGroup>
                            <InputWrapper>
                              <InputLabel>최소 금액</InputLabel>
                              <Input
                                type="text"
                                value={range.minAmount === "1" && idx === 0 ? "1" : formatNumber(range.minAmount)}
                                onChange={(e) => handleBidUnitValueChange(range.index, "minAmount", e.target.value)}
                                placeholder="1"
                                readOnly={idx === 0 || !!(isAllFieldsLocked && mode === 'edit')}
                                hasError={!!rangeErrors.minAmount}
                              />
                              <Unit>원</Unit>
                              {rangeErrors.minAmount && (
                                <ErrorMessage>{rangeErrors.minAmount}</ErrorMessage>
                              )}
                            </InputWrapper>
                            <Separator>~</Separator>
                            <InputWrapper>
                              <InputLabel>최대 금액</InputLabel>
                              <Input
                                type="text"
                                value={isLastRange ? "이상" : formatNumber(range.maxAmount)}
                                onChange={isLastRange ? undefined : (e) => handleBidUnitValueChange(range.index, "maxAmount", e.target.value)}
                                placeholder={isLastRange ? "이상" : "100"}
                                readOnly={isLastRange || !!(isAllFieldsLocked && mode === 'edit')}
                                hasError={!!rangeErrors.maxAmount}
                              />
                              {!isLastRange && <Unit>원</Unit>}
                              {rangeErrors.maxAmount && (
                                <ErrorMessage>{rangeErrors.maxAmount}</ErrorMessage>
                              )}
                            </InputWrapper>
                          </AmountGroup>
                          <UnitGroup>
                            <InputWrapper>
                              <InputLabel>입찰단위</InputLabel>
                              <Input
                                type="text"
                                value={formatNumber(range.unitAmount)}
                                onChange={(e) => handleBidUnitValueChange(range.index, "unitAmount", e.target.value)}
                                placeholder="1000"
                                hasError={!!rangeErrors.unitAmount}
                                readOnly={!!(isAllFieldsLocked && mode === 'edit')}
                              />
                              <Unit>원</Unit>
                              {rangeErrors.unitAmount && (
                                <ErrorMessage>{rangeErrors.unitAmount}</ErrorMessage>
                              )}
                            </InputWrapper>
                          </UnitGroup>
                        </InputGroup>
                      </RangeItem>
                    );
                  })}
                </RangesContainer>
              </BidUnitContent>

              <BidUnitActions>
                <AddRangeButton 
                  onClick={handleAddBidUnitRange}
                  disabled={!!(isAllFieldsLocked && mode === 'edit')}
                  style={{
                    opacity: isAllFieldsLocked && mode === 'edit' ? 0.5 : 1,
                    cursor: isAllFieldsLocked && mode === 'edit' ? 'not-allowed' : 'pointer'
                  }}
                >
                  + 구간 추가
                </AddRangeButton>
              </BidUnitActions>
            </BidUnitContainer>
          </RightPanel>
        </MainContainer>
      </PopupContainer>
      
      {/* 삭제 확인 다이얼로그 */}
      {isDeleteModalOpen && (
        <DeleteModalOverlay onClick={handleDeleteCancel}>
          <DeleteModalContainer onClick={(e) => e.stopPropagation()}>
            <DeleteModalTitle>경매 삭제</DeleteModalTitle>
            <DeleteModalMessage>
              경매를 삭제하시겠습니까?{'\n'}
              삭제된 데이터는 복구할 수 없습니다.
            </DeleteModalMessage>
            <DeleteModalButtons>
              <DeleteModalButton onClick={handleDeleteCancel} variant="cancel">
                취소
              </DeleteModalButton>
              <DeleteModalButton onClick={handleDeleteConfirm} variant="delete">
                삭제
              </DeleteModalButton>
            </DeleteModalButtons>
          </DeleteModalContainer>
        </DeleteModalOverlay>
      )}
      
      <AuctioneerSearchPopup
        isOpen={isAuctioneerPopupOpen}
        onClose={() => setIsAuctioneerPopupOpen(false)}
        onSelect={handleAuctioneerSelect}
      />
    </CmsPopup>
  );
};
